import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { CartLinesUpdateEvent } from '@shopify/events';
import { getWishlist, removeFromWishlist } from '@theme/wishlist';

/**
 * @typedef {object} Refs
 * @property {HTMLTemplateElement} wishlistItemTemplate
 * @property {HTMLUListElement} wishlistList
 * @property {HTMLElement} wishlistEmpty
 * @property {HTMLElement} wishlistCount
 */

/**
 * Renders the wishlist drawer's item list from assets/wishlist.js's localStorage-backed state.
 * Dialog lifecycle (open/close/animation/focus) is owned by <theme-drawer>, same as cart-drawer —
 * this only owns wishlist-specific concerns: rendering, remove, and add-to-cart.
 *
 * @extends {Component<Refs>}
 */
class WishlistDrawerComponent extends Component {
  requiredRefs = ['wishlistItemTemplate', 'wishlistList', 'wishlistEmpty', 'wishlistCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
    this.addEventListener('click', this.onClick);

    // The page's initial wishlist:update (dispatched by wishlist.js's own DOMContentLoaded
    // hydration) may have already fired before this component upgraded — render from the
    // current localStorage state directly rather than waiting for the next change.
    this.render(getWishlist());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
    this.removeEventListener('click', this.onClick);
  }

  /** @param {import('@theme/events').WishlistUpdateEvent} event */
  onWishlistUpdate = (event) => {
    this.render(event.detail.items);
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const removeButton = target.closest('[data-wishlist-remove]');
    if (removeButton) {
      // removeFromWishlist() re-renders synchronously (wishlistList.replaceChildren()),
      // detaching this button from the DOM while the click event is still bubbling.
      // <theme-drawer>'s backdrop-click handler (assets/theme-drawer.js) then sees a
      // detached event.target and — since panel.contains() can only check the live tree —
      // wrongly concludes the click landed outside the panel and closes the drawer.
      // Stopping propagation here keeps the click from ever reaching that listener.
      event.stopPropagation();

      const item = removeButton.closest('[data-wishlist-item]');
      const id = /** @type {HTMLElement | null} */ (item)?.dataset.id;
      if (id) removeFromWishlist(id);
      return;
    }

    const addButton = target.closest('[data-wishlist-add-to-cart]');
    if (addButton instanceof HTMLButtonElement) {
      const item = addButton.closest('[data-wishlist-item]');
      const id = /** @type {HTMLElement | null} */ (item)?.dataset.id;
      const variantId = /** @type {HTMLElement | null} */ (item)?.dataset.variantId;
      if (id && variantId) this.#addToCart(addButton, id, variantId);
    }
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {string} id
   * @param {string} variantId
   */
  async #addToCart(button, id, variantId) {
    button.disabled = true;

    // A personalized product needs its engraving text, which only the product page collects.
    const item = getWishlist().find((entry) => String(entry.id) === id);
    if (item && (await this.#requiresPersonalization(item))) {
      // Re-enabled first so the button still works if Back restores this page from bfcache.
      button.disabled = false;
      window.location.assign(item.url);
      return;
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'wishlist',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      // 'json', not 'javascript': the body is a JSON string, and the 'javascript' config drops
      // Content-Type (it is meant for FormData), which makes /cart/add reject the request.
      const fetchCfg = fetchConfig('json', {
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchCfg).then((res) => res.json());

      if (response.status) {
        deferredEventPromise.reject(new Error(response.message || 'Add to cart failed'));
        button.disabled = false;
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartResponse),
        detail: { items: cartResponse.items, source: 'wishlist-drawer', didError: false },
      });

      // Matches the 2024 theme's behavior: adding from the wishlist removes it from the
      // wishlist. Left in place briefly so the item doesn't vanish before the shopper
      // registers the click landed.
      setTimeout(() => removeFromWishlist(id), 300);
    } catch (error) {
      deferredEventPromise.reject(error);
      button.disabled = false;
    }
  }

  /**
   * Only a stored `true` is trusted. Anything else (a stored `false`, which may be stale if the
   * product was tagged custom after it was wishlisted, or no flag on items saved before it
   * existed) is resolved from the product's current tags with the same rule as
   * snippets/personalization-required.liquid. A failed lookup counts as personalized: opening
   * the product page is harmless, adding a custom piece with no text is not.
   * @param {import('@theme/wishlist').WishlistItem} item
   * @returns {Promise<boolean>}
   */
  async #requiresPersonalization(item) {
    if (item.requiresPersonalization === true) return true;

    try {
      const url = new URL(item.url, window.location.origin);
      const product = await fetch(`${url.pathname}.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );
      /** @type {string[]} */
      const tags = product.tags ?? [];
      if (tags.some((tag) => tag.includes('sorority'))) return false;
      return tags.some(
        (tag) =>
          !tag.includes('custom_phone_case') &&
          (tag.includes('customized') || tag.includes('personalized') || tag.includes('custom'))
      );
    } catch {
      return true;
    }
  }

  /** @param {import('@theme/wishlist').WishlistItem[]} items */
  render(items) {
    const { wishlistItemTemplate, wishlistList, wishlistEmpty, wishlistCount } = this.refs;
    if (!wishlistItemTemplate || !wishlistList) return;

    wishlistList.replaceChildren();

    for (const item of items) {
      const node = /** @type {DocumentFragment} */ (wishlistItemTemplate.content.cloneNode(true));
      const li = /** @type {HTMLElement} */ (node.querySelector('[data-wishlist-item]'));
      li.dataset.id = String(item.id);
      if (item.variantId) li.dataset.variantId = String(item.variantId);

      const addButton = node.querySelector('[data-wishlist-add-to-cart]');
      if (item.requiresPersonalization && addButton instanceof HTMLElement && addButton.dataset.labelPersonalize) {
        addButton.textContent = addButton.dataset.labelPersonalize;
      }

      for (const link of node.querySelectorAll('[data-wishlist-href]')) {
        /** @type {HTMLAnchorElement} */ (link).href = item.url;
      }

      const img = node.querySelector('[data-wishlist-field="image"]');
      if (img instanceof HTMLImageElement) {
        img.src = item.image;
        img.alt = item.title;
      }

      this.#setText(node, 'title', item.title);
      // Shopify's `money` filter output isn't guaranteed plain text — many stores' money
      // format setting wraps it in markup (e.g. `<span class="money">$7.99</span>`), which
      // textContent would show as literal, visible tag characters instead of rendering.
      this.#setHTML(node, 'price', item.price);

      // The wrapper holds the amount and, in the "Comp. value" style, its label, so both show
      // or hide together; only the amount is filled in here.
      const compare = node.querySelector('[data-wishlist-compare]');
      if (compare instanceof HTMLElement) {
        compare.hidden = !item.comparePrice;
        if (item.comparePrice) this.#setHTML(node, 'comparePrice', item.comparePrice);
      }

      wishlistList.appendChild(node);
    }

    if (wishlistEmpty) wishlistEmpty.hidden = items.length > 0;

    // Unlike the header's cart/wishlist badges (hidden at 0), the drawer's own heading always
    // shows the count, "(0)" included — matching the prototype.
    if (wishlistCount) wishlistCount.textContent = String(items.length);
  }

  /**
   * @param {DocumentFragment} node
   * @param {string} field
   * @param {string} value
   */
  #setText(node, field, value) {
    const el = node.querySelector(`[data-wishlist-field="${field}"]`);
    if (el) el.textContent = value;
  }

  /**
   * @param {DocumentFragment} node
   * @param {string} field
   * @param {string} value
   */
  #setHTML(node, field, value) {
    const el = node.querySelector(`[data-wishlist-field="${field}"]`);
    if (el) el.innerHTML = value;
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
